<template>
  <div v-if="blog" :class="$style.container" v-viewer="{ rotatable: false, title: false }">
    <h1 :class="$style.title">{{ pageTitle }}</h1>
    <tabs
      :tabs="tabs"
      :currentTab="currentLanguage"
      :class="$style.tabs"
      @onClick="onTabClick"
    />

    <BaseInput
      v-model="currentTitle"
      :label="$t('post.title')"
      :invalid="$v.currentTitle.$error"
      :error="titleError"
      @blur="$v.currentTitle.$touch"
    />

    <BaseInput
      v-model="currentSubtitle"
      :label="$t('post.subtitle')"
      :placeholder="$t('formOptional')"
      :invalid="$v.currentSubtitle.$error"
      :error="subtitleError"
    />

    <BaseInput
      v-model="currentAuthor"
      :label="$t('post.author')"
      :placeholder="$t('formOptional')"
    />

    <div class="form-container">
      <label class="form--label">{{ $t('post.headerImage') }}</label>
      <FileInput
        v-if="!currentHeaderImgSrc"
        :label="$t('formAttach')"
        accept="image/*"
        @input="onFileChange"
        :class="$style.fileInput"
      >
        <BaseIcon name="paperclip" :class="$style.fileIcon" />
      </FileInput>
      <MediaPreview
        :src="currentHeaderImgSrc"
        @close="clearHeaderImg"
      />
    </div>

    <BaseSelect
      v-model="category"
      :label="$t('category')"
      :placeholder="$t('chooseCategory')"
      :options="categories"
      trackBy="id"
      :optionLabel="localeLabel"
      :invalid="$v.categoryId.$error"
      :error="$t('errorEmptyField')"
    >
      <template v-slot:singleLabel="{ option }">
        <Category :category="option" />
      </template>
      <template v-slot:option="{ option }">
        <Category :category="option" />
      </template>
    </BaseSelect>

    <BaseInput
      multiline
      v-model="currentText"
      :label="$t('post.text')"
      :placeholder="$t('post.textPlaceholder')"
      :invalid="$v.currentText.$error"
      :error="$t('errorEmptyField')"
      @blur="$v.currentText.$touch"
    />

    <div class="form-container">
      <label class="form--label">{{ $t('post.footerImages') }}</label>
      <!-- v-model="currentFooterImageFilesList" -->
      <FileInput
        multiple
        :label="$t('formAttach')"
        accept="image/*"
        @input="onFooterImagesInput"
      >
        <BaseIcon name="paperclip" :class="$style.fileIcon" />
      </FileInput>
      <div v-if="currentFooterImageSrcList.length" :class="$style.footerImages">
        <MediaPreview
          v-for="(imgSrc, i) in currentFooterImageSrcList" :key="i"
          :src="imgSrc"
          :class="$style.footerImage"
          @close="deleteFooterImgFromList(i)"
        />
      </div>
    </div>
    <div class="form-container">
      <label class="form--label">{{ $t('post.footerVideos') }}</label>
      <FileInput
        multiple
        :label="$t('formAttach')"
        accept="video/*"
        @input="onFooterVideosInput"
      >
        <BaseIcon name="paperclip" :class="$style.fileIcon" />
      </FileInput>
      <div v-if="currentFooterVideoSrcList.length" :class="$style.footerImages">
        <MediaPreview
          v-for="(src, i) in currentFooterVideoSrcList" :key="i"
          :src="src"
          isVideo
          :class="$style.footerImage"
          @close="deleteFooterVideoFromList(i)"
        />
      </div>
    </div>

    <BaseSwitch v-model="isPublished" class="form-container">{{ $t('post.isPublished') }}</BaseSwitch>

    <BaseButton
      oval
      :loading="loading"
      @click="onSubmit"
      :class="$style.button"
    >{{ submitButtonTxt }}</BaseButton>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Category from '@/components/Category'
import { required, maxLength } from 'vuelidate/lib/validators'
import FileInput from '@/components/FileInput'
import MediaPreview from '@/components/MediaPreview'
import api from '@/api'
import { camelCase, capitalize, snakeCase, get } from 'lodash'
import { LOCALES } from '@/utils/config'
import Tabs from 'vue-tabs-with-active-line'
import imageCompression from 'browser-image-compression'
import { nanoid } from 'nanoid'

const TITLE_MAX_LENGTH = 100
const SUBTITLE_MAX_LENGTH = 200

export default {
  name: 'blog-create-post',
  components: {
    Category,
    FileInput,
    MediaPreview,
    Tabs
  },
  props: {
    blogId: {
      required: true,
      type: String
    }
  },
  async created () {
    this.getPostCategories()
    if (!this.blog) {
      await this.fetchBlog(this.blogId)
    }
    this.currentLanguage = this.blog.defaultLanguage
    Object.keys(LOCALES).forEach(key => {
      const lang = snakeCase(key)
      const blogInfoObj = this.blog.blogInfo.find(obj => obj.language === lang)
      if (blogInfoObj) {
        this.$set(this.postByLang[lang], 'blogInfo', blogInfoObj.id)
      } else {
        this.$delete(this.postByLang, lang)
      }
    })
  },
  data () {
    const currentLanguage = snakeCase(this.$root.$i18n.locale)
    const postByLang = {}
    Object.keys(LOCALES).forEach(key => {
      postByLang[snakeCase(key)] = {
        title: '',
        subtitle: '',
        author: '',
        text: '',
        headerImgSrc: null,
        // footerImageFilesList: null,
        footerImageSrcList: [],
        footerVideoSrcList: [],
        footerVideoFilesList: []
      }
    })
    return {
      pageTitle: this.$t('post.createTitle'),
      submitButtonTxt: this.$t('buttonCreate'),
      category: null,
      isPublished: true,
      loading: false,
      currentLanguage,
      postByLang
    }
  },
  computed: {
    ...mapGetters({
      categories: 'entities/postCategories/list',
      blogById: 'entities/blogs/getBlog' // 'entities/blogs/find'
    }),
    tabs () {
      if (!this.blog) return []
      return this.blog.blogInfo.map(obj => ({
        title: capitalize(obj.language),
        value: obj.language
      }))
    },
    localeLabel () {
      return camelCase(this.$i18n.locale) // zhHans
    },
    blog () {
      return this.blogById(this.blogId)
    },
    categoryId () {
      return this.category ? this.category.id : null
    },
    titleError () {
      if (!this.$v.currentTitle.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${TITLE_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return this.$t('errorEmptyField')
    },
    subtitleError () {
      if (!this.$v.currentSubtitle.maxLength) {
        return `${this.$t('errorMaxLength_begin')} ${SUBTITLE_MAX_LENGTH} ${this.$t('errorLength_end')}`
      }
      return ''
    },
    currentTitle: {
      get () {
        return this.postByLang[this.currentLanguage].title
      },
      set (value) {
        this.postByLang[this.currentLanguage].title = value
      }
    },
    currentSubtitle: {
      get () {
        return this.postByLang[this.currentLanguage].subtitle
      },
      set (value) {
        this.postByLang[this.currentLanguage].subtitle = value
      }
    },
    currentAuthor: {
      get () {
        return this.postByLang[this.currentLanguage].author
      },
      set (value) {
        this.postByLang[this.currentLanguage].author = value
      }
    },
    currentText: {
      get () {
        return this.postByLang[this.currentLanguage].text
      },
      set (value) {
        this.postByLang[this.currentLanguage].text = value
      }
    },
    currentHeaderImgSrc: {
      get () {
        return this.postByLang[this.currentLanguage].headerImgSrc
      },
      set (value) {
        this.postByLang[this.currentLanguage].headerImgSrc = value
      }
    },
    // currentFooterImageFilesList: {
    //   get () {
    //     return this.postByLang[this.currentLanguage].footerImageFilesList
    //   },
    //   set (value) {
    //     this.postByLang[this.currentLanguage].footerImageFilesList = value
    //   }
    // },
    currentFooterImageSrcList: {
      get () {
        return this.postByLang[this.currentLanguage].footerImageSrcList
      },
      set (value) {
        this.postByLang[this.currentLanguage].footerImageSrcList = value
      }
    },
    currentFooterVideoSrcList: {
      get () {
        return this.postByLang[this.currentLanguage].footerVideoSrcList
      },
      set (value) {
        this.postByLang[this.currentLanguage].footerVideoSrcList = value
      }
    },
    currentFooterVideoFilesList: {
      get () {
        return this.postByLang[this.currentLanguage].footerVideoFilesList
      },
      set (value) {
        this.postByLang[this.currentLanguage].footerVideoFilesList = value
      }
    }
  },
  watch: {
    // currentFooterImageFilesList: 'getMultipleSrc',
    currentLanguage () {
      this.$v.currentTitle.$reset()
      this.$v.currentText.$reset()
    }
  },
  validations: {
    currentTitle: { required, maxLength: maxLength(TITLE_MAX_LENGTH) },
    currentSubtitle: { maxLength: maxLength(SUBTITLE_MAX_LENGTH) },
    currentText: { required },
    categoryId: { required }
  },
  methods: {
    ...mapActions({
      createPost: 'entities/posts/createPost',
      fetchBlog: 'entities/blogs/fetchByShortId',
      getPostCategories: 'entities/postCategories/fetch'
    }),
    onTabClick (newTab) {
      this.currentLanguage = newTab
    },
    async onFooterImagesInput (fileList) {
      if (!fileList) return
      for (const file of fileList) {
        if (!file) continue
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 1
        })
        const src = await imageCompression.getDataUrlFromFile(compressedFile)
        this.currentFooterImageSrcList.push(src)
      }
      // this.currentFooterImageFilesList = null
    },
    onFooterVideosInput (fileList) {
      for (const file of fileList) {
        const blobURL = URL.createObjectURL(file)
        this.currentFooterVideoSrcList.push(blobURL)
        this.currentFooterVideoFilesList.push(file)
      }
    },
    async onFileChange (file) {
      this.$modal.show('cropper', {
        aspectRatio: 16 / 9,
        file,
        onDone: canvas => {
          this.currentHeaderImgSrc = canvas.toDataURL('image/jpeg')
        }
      })
    },
    clearHeaderImg () {
      this.currentHeaderImgSrc = null
    },
    deleteFooterImgFromList (i) {
      this.currentFooterImageSrcList.splice(i, 1)
    },
    deleteFooterVideoFromList (i) {
      this.currentFooterVideoSrcList.splice(i, 1)
      this.currentFooterVideoFilesList.splice(i, 1)
    },
    async submitPhotos (lang, postUuid) {
      const reqList = []
      const headerImgSrc = this.postByLang[lang].headerImgSrc
      const footerImageSrcList = this.postByLang[lang].footerImageSrcList
      if (headerImgSrc) {
        const image = await imageCompression.getFilefromDataUrl(headerImgSrc, 'post-header-image')
        const compressedBlob = await imageCompression(image, {
          maxSizeMB: 1
        })
        const compressedFile = new File([compressedBlob], `${compressedBlob.name}.${compressedBlob.type.split('/')[1]}`)
        reqList.push(
          api.blogs.savePostImage(postUuid, {
            position: 1,
            order: 1,
            image: compressedFile
          })
        )
      }
      if (footerImageSrcList) {
        footerImageSrcList.forEach(async (src, index) => {
          const blob = await imageCompression.getFilefromDataUrl(src, 'post-image')
          const file = new File([blob], `${blob.name}.${blob.type.split('/')[1]}`)
          reqList.push(
            api.blogs.savePostImage(postUuid, {
              position: 2,
              order: index + 1,
              image: file
            })
          )
        })
      }
      await Promise.all(reqList)
    },
    async submitVideos (lang, postUuid) {
      const reqList = []
      const footerVideoFilesList = this.postByLang[lang].footerVideoFilesList
      if (!footerVideoFilesList || !footerVideoFilesList.length) return
      footerVideoFilesList.forEach((file, index) => {
        reqList.push(
          api.blogs.savePostVideo(postUuid, {
            position: 2,
            order: index + 1,
            video: file
          })
        )
      })
      await Promise.all(reqList)
    },
    async onSubmit () {
      this.$v.$touch()
      if (this.loading || this.$v.$invalid) return
      this.loading = true
      try {
        let defaultPost
        const relationId = nanoid()
        const handleSinglePost = async lang => {
          const { title, subtitle, author, text, blogInfo } = this.postByLang[lang]
          if (!title || !text) return
          const post = await this.createPost({
            blogUuid: this.blog.uuid,
            data: {
              relationId,
              language: lang,
              title,
              subtitle,
              author,
              text,
              categoryId: this.categoryId,
              isPublished: this.isPublished,
              blogInfo
            }
          })
          if (lang === this.blog.defaultLanguage) {
            defaultPost = post
          }
          await this.submitPhotos(lang, post.uuid)
          await this.submitVideos(lang, post.uuid)
        }
        await Promise.all(
          Object.keys(this.postByLang).map(handleSinglePost)
        )
        if (defaultPost) {
          this.$router.push({
            name: 'app-blogs-post',
            params: {
              blogId: this.blogId,
              postId: defaultPost.uuid
            }
          })
        } else {
          this.$router.push({
            name: 'app-blogs',
            params: {
              blogId: this.blogId
            }
          })
        }
      } catch (e) {
        const error = get(e, 'response.data') || e.message
        this.$modal.show('error', { error })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style module lang="scss">
.container {
  padding: 0 25px;
  .button {
    margin: 0 auto;
  }
}
.tabs:global(.tabs) {
  margin-bottom: 12px;
  :global(.tabs__item) {
    flex-grow: unset;
    flex-basis: auto;
  }
}
.title {
  font-size: 18px;
  font-weight: 500;
  color: $text-success;
  margin: 0;
  margin-bottom: 16px;
  text-align: center;
}
.fileIcon {
  margin-right: 8px;
  color: $text-success;
}
.footerImages {
  display: flex;
  flex-wrap: wrap;
}
.footerImage {
  margin-right: 10px;
  margin-bottom: 10px;
}
</style>
